import { useState, useEffect } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { MessageSquare, Mail, Clock, User } from "lucide-react";

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const response = await api.get("/admin/messages");
        setMessages(response.data);
      } catch (error) {
        console.error("Error fetching messages:", error);
      }
      setLoading(false);
    };

    fetchMessages();
  }, []);

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "MMMM d, yyyy 'at' HH:mm");
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Messages">
        <div className="h-96 flex items-center justify-center">
          <div className="loading-spinner" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Messages">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Messages List */}
        <div className="lg:col-span-1 space-y-2">
          {messages.length === 0 ? (
            <div className="empty-state py-12">
              <MessageSquare className="w-12 h-12 text-slate-700 mb-4" />
              <h3 className="text-lg font-bold mb-2">No Messages</h3>
              <p className="text-sm text-muted-foreground">
                You haven't received any messages yet
              </p>
            </div>
          ) : (
            messages.map((message, index) => (
              <motion.button
                key={message.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => setSelectedMessage(message)}
                data-testid={`message-${message.id}`}
                className={`w-full text-left p-4 rounded-xl border transition-colors ${
                  selectedMessage?.id === message.id
                    ? "bg-primary/10 border-primary/50"
                    : "bg-slate-900/50 border-slate-800 hover:border-slate-700"
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <span className="font-bold text-sm truncate flex-1 mr-2">
                    {message.name}
                  </span>
                  {!message.is_read && (
                    <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0" />
                  )}
                </div>
                <p className="text-xs text-muted-foreground truncate mb-1">
                  {message.subject}
                </p>
                <p className="text-xs text-muted-foreground/60">
                  {formatDate(message.created_at)}
                </p>
              </motion.button>
            ))
          )}
        </div>

        {/* Message Detail */}
        <div className="lg:col-span-2">
          {selectedMessage ? (
            <motion.div
              key={selectedMessage.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-900/50 border border-slate-800 rounded-xl p-6"
            >
              {/* Header */}
              <div className="border-b border-slate-800 pb-4 mb-4">
                <h2 className="text-xl font-bold mb-4">{selectedMessage.subject}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <User className="w-4 h-4" />
                    <span>{selectedMessage.name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Mail className="w-4 h-4" />
                    <a
                      href={`mailto:${selectedMessage.email}`}
                      className="text-primary hover:underline"
                    >
                      {selectedMessage.email}
                    </a>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    <span>{formatDate(selectedMessage.created_at)}</span>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="prose prose-invert prose-sm max-w-none">
                <p className="text-foreground whitespace-pre-wrap leading-relaxed">
                  {selectedMessage.message}
                </p>
              </div>

              {/* Reply Button */}
              <div className="mt-6 pt-4 border-t border-slate-800">
                <a
                  href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm uppercase tracking-wider hover:bg-primary/90 transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  Reply
                </a>
              </div>
            </motion.div>
          ) : (
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl h-full min-h-[300px] flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="w-12 h-12 text-slate-700 mx-auto mb-4" />
                <p className="text-muted-foreground">
                  Select a message to view details
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminMessages;
